/* Footer, ported from the designer's `footerMarkup.js` and its stylesheet. The
   published html_content never carries a footer — the designer strips it from the
   export — so every rule the markup needs is stated here.

   Text hovers go to near-black, SP-main's own treatment. The storefront orange
   belongs to buttons; a footer whose every link turns orange reads as a column
   of warnings. */
import { DEFAULT_THEME } from './bannerMarkup';

export const FOOTER_STYLES = `
  .sf-foot {
    width: 100%;
    box-sizing: border-box;
    background: #FAFBFB;
    border-top: 1px solid #E5E7E8;
    padding: 44px 0 26px;
  }
  .sf-foot-in { padding: 0 40px; width: 100%; box-sizing: border-box; }
  .sf-foot * { box-sizing: border-box; }
  /* ⚠ Flex, not a fixed track count — SP-main's own layout and its own warning.

     This was three hard tracks (minmax(0,3.5fr) 1fr 1fr), so each link column
     took a fixed share of the row and stretched with the window instead of
     holding a readable measure: 323px at 1920, 160px at 1024. The reference pins
     them at 200px. (It hit the same bug from the other side: removing its email
     sign-up left three items in four tracks and 379px of dead space.)

     ⛔ Do not put a track count back. The Contact column is only drawn when the
     partner filled in an address, phone or email, so this footer is two columns
     for some stores and three for others, and a layout that depends on a count
     breaks when the count changes. margin-right:auto on the identity block holds
     the links to the right edge either way. */
  .foot-top {
    display: flex;
    gap: 32px;
    flex-wrap: wrap;
    align-items: flex-start;
  }
  .foot-brand { flex: 1 1 380px; margin-right: auto; min-width: 0; max-width: 100%; }
  .foot-col { flex: 0 1 200px; max-width: 100%; overflow-wrap: break-word; }
  .foot-brand .fb-row { display: flex; align-items: center; gap: 10px; min-width: 0; }
  .foot-logo {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    display: grid;
    place-items: center;
    flex: none;
    overflow: hidden;
    position: relative;
  }
  .foot-logo img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: contain; }
  .foot-initials {
    color: #FFFFFF;
    font-size: 13.5px;
    font-weight: 700;
    letter-spacing: 0.05em;
  }
  /* Scoped under .sf-foot so it outranks the designer's own .foot-name rule,
     which rides along in the published html_content's stylesheet. */
  .sf-foot .foot-name {
    font-weight: 700;
    font-size: 19px;
    line-height: 1.3;
    letter-spacing: -0.01em;
    color: #2C2F32;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .foot-msg {
    font-size: 13px;
    color: #565E64;
    margin: 12px 0 0;
    max-width: 250px;
    line-height: 1.5;
  }
  .foot-power { font-size: 12px; color: #808B90; margin-top: 14px; }
  .foot-power b { color: #2C2F32; font-weight: 700; }

  /* Shop holds the third track whether or not Contact renders, rather than
     leaning on an empty cell to push it there. */
  @media (min-width: 861px) {
    .foot-shop { grid-column: 3; }
  }

  .foot-col h4 {
    font-size: 11px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #808B90;
    margin: 0 0 14px;
    font-weight: 600;
  }
  .foot-col a {
    display: block;
    font-size: 13.5px;
    color: #565E64;
    margin-bottom: 10px;
    text-decoration: none;
    transition: color .14s;
  }
  /* Stated through .sf-foot rather than a bare ".foot-col a:hover" so the
     :focus-visible half is not left to chance: the canvas sheet travels inside
     html_content into this page's BODY and would tie any bare selector here.
     Same for .foot-legal and .foot-s below. */
  .sf-foot .foot-col a:hover,
  .sf-foot .foot-col a:focus-visible { color: #2C2F32; }
  .sf-foot .foot-col a:focus-visible { outline: 2px solid ${DEFAULT_THEME}; outline-offset: 2px; border-radius: 3px; }
  .foot-line {
    display: block;
    font-size: 13.5px;
    color: #565E64;
    margin-bottom: 10px;
  }
  .foot-bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 32px;
    padding-top: 18px;
    border-top: 1px solid #E5E7E8;
    font-size: 12.5px;
    color: #808B90;
    flex-wrap: wrap;
    gap: 12px;
  }
  .foot-legal { display: flex; flex-wrap: wrap; gap: 18px; }
  .foot-legal a { color: #808B90; text-decoration: none; transition: color .14s; }
  .sf-foot .foot-legal a:hover,
  .sf-foot .foot-legal a:focus-visible { color: #2C2F32; }
  .sf-foot .foot-legal a:focus-visible { outline: 2px solid ${DEFAULT_THEME}; outline-offset: 2px; border-radius: 3px; }

  /* Plain glyphs sitting with the brand, not bordered chips in the legal row: a
     row of boxed icons opposite the copyright reads as a second footer. */
  .foot-social {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 14px;
    /* A store can set six of these; without wrapping the row pushes past the
       identity column on a phone instead of dropping to a second line. */
    flex-wrap: wrap;
  }
  .foot-s {
    display: grid;
    place-items: center;
    color: #565E64;
    width: 20px;
    height: 20px;
    transition: color .14s;
  }
  .sf-foot .foot-s:hover,
  .sf-foot .foot-s:focus-visible { color: #2C2F32; }
  .sf-foot .foot-s:focus-visible { outline: 2px solid ${DEFAULT_THEME}; outline-offset: 3px; border-radius: 4px; }
  /* The glyph fills its 20px box. At 18px inside a 20px well every social mark
     drew a touch smaller than the reference's. */
  .foot-s svg { width: 100%; height: 100%; display: block; }

  /* Powered-by tucks closer when the icons already opened the gap. */
  .foot-social + .foot-power { margin-top: 10px; }

  @media (max-width: 860px) {
    .sf-foot { padding: 32px 0 22px; }
    .sf-foot-in { padding: 0 20px; }
    /* The stack is flex-wrap's, not a track override: the old rule here set
       grid-template-columns, which does nothing to a flex container. Giving every
       block the full row is what stacks them. */
    .foot-top { gap: 26px; }
    .foot-brand, .foot-col { flex: 1 1 100%; margin-right: 0; }
    .foot-msg { max-width: none; }
    .foot-bottom { align-items: flex-start; }
  }
`;
